<template>
  <v-layout xs12 sm12 row justify-center>
    <v-dialog v-model="dialog" fullscreen transition="dialog-bottom-transition" scrollable>
      <v-card>
        <v-toolbar dark color="primary">
          <v-btn icon dark @click.native="close">
            <v-icon>close</v-icon>
          </v-btn>

          <v-toolbar-title>{{ $t('toolbar_title')}}</v-toolbar-title>

          <v-spacer></v-spacer>

          <v-toolbar-items>
            <v-btn dark flat @click.native="save">{{ $t('save')}}</v-btn>
          </v-toolbar-items>
        </v-toolbar>

        <v-card-text>
          <v-form ref="form" v-model="valid" lazy-validation>
            <v-card flat>
              <v-layout row wrap>
                <v-flex xs12 sm6 md3 d-flex pa-3>
                  <v-card>
                    <v-card-text>
                      <v-flex v-if="configuration.protection" xs12 sm6 md4>
                        <v-switch v-model="editedItem.protected">
                          <template slot="label">
                            <span v-if="editedItem.protected">{{$t('unprotect_label')}}</span>
                            <span v-else>{{$t('protect_label')}}</span>
                          </template>
                        </v-switch>
                      </v-flex>

                      <v-flex v-if="configuration.auditname">
                        <v-text-field
                          v-model="editedItem.name"
                          :label="$t('auditname_label')"
                          :rules="rule_name"
                        ></v-text-field>
                      </v-flex>

                      <!-- TODO: adding multiple standards leads to long tailed adjustments...-->

                      <v-flex v-if="configuration.standard">
                        <v-autocomplete
                          :items="selectableStandards"
                          item-value="id"
                          item-text="name"
                          v-model="editedItem.standard"
                          :label="$t('standard_label')"
                          persistent-hint
                          required
                          multiple
                        ></v-autocomplete>
                      </v-flex>

                      <v-flex v-if="configuration.type">
                        <v-autocomplete
                          :items="selectableAuditTypes"
                          v-model="editedItem.type"
                          :label="$t('audittype_label')"
                          persistent-hint
                          required
                        ></v-autocomplete>
                      </v-flex>

                      <v-flex v-if="configuration.audited_by">
                        <v-autocomplete
                          :items="selectableAuditBys"
                          v-model="editedItem.audited_by"
                          :label="$t('auditedby_label')"
                          persistent-hint
                          required
                        ></v-autocomplete>
                      </v-flex>
                      <v-flex v-if="hasAuditReason && configuration.audit_reason">
                        <v-text-field
                          v-model="editedItem.audit_reason"
                          :label="$t('audit_reason')"
                          persistent-hint
                        />
                      </v-flex>

                      <v-flex v-if="hasSupplierMetadata && configuration.metadata_supplier">
                        <v-text-field
                          v-model="supplierMetadata.name"
                          :label="$t('supplier_name')"
                          persistent-hint
                        />
                        <v-textarea
                          auto-grow
                          v-model="supplierMetadata.location"
                          :label="$t('supplier_location')"
                          rows="3"
                          persistent-hint
                        />
                        <v-text-field
                          v-model="supplierMetadata.no"
                          :label="$t('supplier_no')"
                          persistent-hint
                        />
                      </v-flex>
                    </v-card-text>
                  </v-card>
                </v-flex>

                <v-flex xs12 sm6 md3 d-flex pa-3>
                  <v-card>
                    <v-card-text>
                      <ScopeSelect
                        :scope="editedItem.scope"
                        v-if="configuration.scope && configuration.complexscope"
                        :audittype="editedItem.type"
                        @update:scope="onScopeUpdate"
                      />

                      <!-- TODO Implement "simple scope" within scope component" -->
                      <v-flex v-if="configuration.scope && !configuration.complexscope">
                        <v-autocomplete
                          :items="selectableScopes"
                          v-model="editedItem.scope"
                          :label="$t('auditscope_label')"
                          chips
                          multiple
                          persistent-hint
                        ></v-autocomplete>
                      </v-flex>

                      <v-flex
                        v-if="(editedItem.type=='Prozessaudit' || editedItem.type=='Begehung' ) && (configuration.process && !configuration.complexscope)"
                      >
                        <v-combobox
                          v-model="editedItem.process"
                          :items="selectableProcesses"
                          chips
                          deletable-chips
                          :label="$t('process_label')"
                          @input="$emit('update:scope', scope)"
                        ></v-combobox>
                      </v-flex>

                      <v-flex v-if="hasMaterialMetadata && configuration.metadata_material">
                        <v-text-field
                          v-model="materialMetadata.group"
                          :label="$t('material_group')"
                          persistent-hint
                        />
                        <v-text-field
                          v-model="materialMetadata.id"
                          :label="$t('material_id')"
                          persistent-hint
                        />
                      </v-flex>
                    </v-card-text>
                  </v-card>
                </v-flex>

                <v-flex xs12 sm6 md3 d-flex pa-3>
                  <v-card>
                    <v-card-text>
                      <v-flex v-if="configuration.interestedparties">
                        <v-autocomplete
                          :items="selectableAuditBys"
                          v-model="editedItem.interestedparties"
                          :label="$t('interestedparties_label')"
                          persistent-hint
                        ></v-autocomplete>
                      </v-flex>

                      <v-flex v-if="configuration.leadauditor">
                        <v-autocomplete
                          :items="selectableAuditors"
                          item-text="displayName"
                          item-value="id"
                          item-avatar="avatar"
                          v-model="editedItem.leadauditor"
                          :label="$t('leadauditor_label')"
                          chips
                          :rules="rule_year"
                        ></v-autocomplete>
                      </v-flex>

                      <v-flex v-if="configuration.coauditors">
                        <v-autocomplete
                          :items="selectableAuditors"
                          item-text="displayName"
                          item-value="id"
                          item-avatar="avatar"
                          v-model="editedItem.coauditors"
                          :label="$t('coauditors_label')"
                          multiple
                          chips
                          deletable-chips
                        ></v-autocomplete>
                      </v-flex>

                      <v-flex v-if="configuration.responsible">
                        <UserAutocomplete
                          :label="$t('responsible_label')"
                          :selectedUser.sync="editedItem.responsible"
                          chips
                        />
                      </v-flex>

                      <v-flex v-if="configuration.audit_participants">
                        <UserAutocomplete
                          :label="$t('auditparticipants_label')"
                          :selectedUser.sync="editedItem.audit_participants"
                          multiple
                          chips
                          small-chips
                          deletable-chips
                        />
                      </v-flex>
                      <v-flex
                        v-if="hasAdditionalResponsibles && configuration.additional_responsibles"
                      >
                        <UserAutocomplete
                          :label="$t('responsible_tech_label')"
                          :selectedUser.sync="editedItem.responsible_tech"
                          chips
                        />
                        <UserAutocomplete
                          :label="$t('responsible_sales_label')"
                          :selectedUser.sync="editedItem.responsible_sales"
                          chips
                        />
                        <UserAutocomplete
                          :label="$t('responsible_qm_label')"
                          :selectedUser.sync="editedItem.responsible_qm"
                          chips
                        />
                      </v-flex>
                    </v-card-text>
                  </v-card>
                </v-flex>

                <v-flex xs12 sm6 md3 d-flex pa-3>
                  <v-card>
                    <v-card-text>
                      <v-flex v-if="configuration.planning_year">
                        <v-select
                          :items="selectablePlanningYears"
                          v-model="editedItem.planning_year"
                          :label="$t('planning_year_label')"
                          required
                          :rules="rule_year"
                        ></v-select>
                      </v-flex>

                      <v-flex v-if="configuration.planning_date">
                        <MenuDatePicker
                          :label="$t('planning_date_label')"
                          :min="currentMinimumDate"
                          :max="currentMaximumDate"
                          v-model="editedItem.planning_date"
                          clearable
                        />
                      </v-flex>
                      <v-flex v-if="configuration.auditing_date">
                        <MenuDatePicker
                          :label="$t('auditing_date_label')"
                          :min="currentMinimumDate"
                          :max="currentMaximumDate"
                          v-model="editedItem.auditing_date"
                          clearable
                          multiple
                        />
                      </v-flex>
                    </v-card-text>
                  </v-card>
                </v-flex>

                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn color="blue darken-1" flat @click.native="close">{{$t('cancel')}}</v-btn>
                  <v-btn color="blue darken-1" flat @click.native="save">{{$t('save')}}</v-btn>
                </v-card-actions>
              </v-layout>
            </v-card>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-layout>
</template>

<i18n>
{
  "de": {
    "toolbar_title": "Audit Metadaten",
    "protect_label": "Audit schützen",
    "unprotect_label": "Audit geschützt",
    "auditname_label": "Audit Name",
    "standard_label": "Auditerter Standard",
    "process_label": "Auditerter Prozess",
    "audittype_label": "Audit Art",
    "interestedparties_label": "Interessierte Parteien",
    "auditedby_label": "Audit Typ",
    "auditscope_label": "Auditierter Bereich",
    "coauditors_label": "Co-Auditoren",
    "leadauditor_label": "Lead-Auditor",
    "auditparticipants_label": "Audit Teilnehmer",
    "responsible_label": "Verantwortlich für die Maßnahmen",
    "planning_year_label": "Planungsjahr",
    "planning_date_label": "Datum der Planung",
    "auditing_date_label": "Datum der Durchführung",
    "status_label": "Status im Auditprozess",
    "supplier_name" : "Lieferantenname",
    "supplier_location" : "Lieferantenstandort",
    "supplier_no" : "Lieferantennummer",
    "material_group" : "Materialgruppe",
    "material_id" : "Material",
    "audit_reason" : "Auditgrund",
    "responsible_tech_label" : "Zuständiger Technik",
    "responsible_sales_label" : "Zuständiger Einkauf",
    "responsible_qm_label" : "Zuständiger QM",
    "save": "Speichern",
    "cancel": "Abbrechen"
  },
  "en": {
    "auditfinding_title": "Audit Finding",
    "auditfinding_label": "Description of the Finding"  
  }
}
</i18n>

<script>
// Controls
import ScopeSelect from "@/components/controls/ScopeSelect.vue";
import UserAutocomplete from "@/components/controls/UserAutocomplete.vue";
import MenuDatePicker from "@/components/controls/MenuDatePicker";

import { mapActions, mapGetters } from "vuex";
import { api } from "@/store/modules/audit";
import { api as userApi } from "@/store/modules/users";
import { api as confApi } from "@/store/modules/configuration";
import { api as standardApi } from "@/store/modules/standards";
import uuid from "uuid";

import Vue from "vue";

// import moment from "moment";
import _ from "lodash";

const emptySupplierMetadata = () => {
  return {
    location: null,
    no: null,
    name: null
  };
};

const emptyMaterialMetadata = () => {
  return {
    group: null,
    id: null
  };
};

export default {
  props: {
    dialog: {
      type: Boolean,
      default: true
    },
    dialogData: {
      type: Object,
      default() {
        return {
          id: null,
          metadata: {}
        };
      }
    }
  },
  data: () => ({
    menu1: false,
    menu2: false,
    today: null,
    valid: true,
    supplierMetadata: emptySupplierMetadata(),
    materialMetadata: emptyMaterialMetadata(),
    rule_name: [
      //TODO: Warum gibt es diesen Bug?
      //  v => v.length <= 140 || "Max 140 characters",
      v => !!v || "Item is required"
    ],
    rule_year: [v => !!v || "Item is required"],
    editedItem: null
  }),
  components: {
    ScopeSelect,
    UserAutocomplete,
    MenuDatePicker
  },
  created() {
    this.editedItem = _.cloneDeep(this.dialogData.metadata);

    if (typeof this.editedItem.metadata === "undefined") {
      Vue.set(this.editedItem, "metadata", {});
    }
    if (this.hasSupplierMetadata && this.editedItem.metadata.supplier) {
      this.supplierMetadata = this.editedItem.metadata.supplier;
    }
    if (this.hasMaterialMetadata && this.editedItem.metadata.material) {
      this.materialMetadata = this.editedItem.metadata.material;
    }
  },
  computed: {
    ...mapGetters(userApi.namespace, [userApi.getters.getLoadedUsersByRole]),
    ...mapGetters(standardApi.namespace, [standardApi.getters.getStandardList]),
    ...mapGetters(confApi.namespace, [confApi.getters.audit]),
    configuration() {
      return this.audit.dialog || {};
    },

    hasSupplierMetadata() {
      return (
        this.configuration.audited_by &&
        this.editedItem.audited_by == "Lieferant"
      );
    },
    hasMaterialMetadata() {
      return (
        this.configuration.audited_by &&
        this.editedItem.audited_by == "Lieferant"
      );
    },
    hasAuditReason() {
      return (
        this.configuration.audited_by &&
        this.editedItem.audited_by == "Lieferant"
      );
    },
    hasAdditionalResponsibles() {
      return (
        this.configuration.audited_by &&
        this.editedItem.audited_by == "Lieferant"
      );
    },
    isUpdate() {
      if (this.getDialogAction === "update") {
        return true;
      } else {
        return false;
      }
    },
    selectableScopes() {
      return this.audit.data.scopes;
    },
    selectablePlanningYears() {
      return [2019, 2020, 2021, 2022];
    },
    currentMinimumDate() {
      let year = this.editedItem.planning_year || "1970";

      let z = new Date("1970-01-01");
      z.setFullYear(year);
      return z.toISOString().substr(0, 10);
    },
    currentMaximumDate() {
      let year = this.editedItem.planning_year || "1970";

      let z = new Date("1970-12-31");
      z.setFullYear(year);
      return z.toISOString().substr(0, 10);
    },
    selectableAuditTypes() {
      return this.audit.data.types;
    },
    selectableStandards() {
      console.log("StandardList", this.getStandardList);
      return this.getStandardList;
    },
    selectableProcesses() {
      return this.audit.data.processes;
    },
    selectableAuditBys() {
      return this.audit.data.auditparties;
    },
    selectableAuditors() {
      return this.getLoadedUsersByRole("auditor");
    }
  },
  methods: {
    ...mapActions(api.namespace, [
      api.actions.createAudit,
      api.actions.updateAuditMetadata
    ]),
    onScopeUpdate(evt) {
      this.editedItem.scope = evt;
    },
    close(redirectAuditId) {
      this.$emit("update:dialog", false);
      if (typeof redirectAuditId === "string") {
        this.$router.push(`/audit/${redirectAuditId}`);
      }
    },
    save() {
      if (this.$refs.form.validate()) {
        if (this.hasSupplierMetadata) {
          if (this.editedItem.metadata) {
            Vue.set(
              this.editedItem.metadata,
              "supplier",
              this.supplierMetadata
            );
          } else {
            Vue.set(this.editedItem, "metadata", {
              supplier: this.supplierMetadata
            });
          }
        } else if (
          this.editedItem.metadata &&
          this.editedItem.metadata.supplier
        ) {
          Vue.delete(this.editedItem.metadata, "supplier");
        }
        if (this.hasMaterialMetadata) {
          Vue.set(this.editedItem.metadata, "material", this.materialMetadata);
        } else if (
          this.editedItem.metadata &&
          this.editedItem.metadata.material
        ) {
          Vue.delete(this.editedItem.metadata, "material");
        }

        const activity_creator = (auditId, create) => ({
          id: uuid.v4(),
          avatar: this.$user.avatar(),
          actor: this.$user.id(),
          actor_name: this.$user.displayName(),
          verb:
            this.$user.displayName() +
            " hat das Audit " +
            this.editedItem.name +
            (create ? " erstellt" : " bearbeitet"),
          object: auditId,
          object_url: "/audit/" + auditId,
          timestamp: new Date().toISOString().slice(0, 10)
        });

        this.editedItem.responsible = this.editedItem.responsible || null;

        if (this.dialogData.id === null) {
          this.createAudit({
            data: this.editedItem,
            activity: activity_creator
          }).then(docRef => {
            console.log("CREATE_AUDIT_SUCCEEDED", docRef);
            this.close(docRef.id);
          });
        } else {
          this.updateAuditMetadata({
            activity: activity_creator(this.dialogData.id),
            id: this.dialogData.id,
            data: this.editedItem
          }).then(() => {
            console.log("UPDATE_AUDIT_SUCCEEDED");
          });
          this.close();
        }
      }
    }
  }
};
</script>
